import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { config } from './config'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { useNavigate } from 'react-router-dom'
import { PageLoading } from '../../components/PageLoading'
import { AuthError } from '@aws-amplify/auth/src/Errors'
import { logger } from './logger'

interface IAuthContext {
    user: CognitoUser | null
    auth: typeof Auth
    setUser: (user: CognitoUser | null) => void
    linkingCompleted: boolean
    clearLinkingCompleted: () => void
    federatedSigninLoading: boolean
    setFederatedSigninLoading: (boolean) => void
    previousSignin: boolean
    setPreviousSignin: () => void
}
export const AuthContext = React.createContext<IAuthContext>({} as IAuthContext)

export const PREVIOUS_SIGNIN_KEY = 'PREVIOUS_SIGNIN'

const setLoginTime = () => {
    localStorage.setItem('loginTime', new Date().getTime().toString())
}

export const getLoginTime = () => {
    let time = new Date().getTime()
    try {
        time = Number(localStorage.getItem('loginTime'))
    } catch (e: any) {
        logger.exception(e)
    }
    return time
}

export const getSecondsSinceLogin = () => {
    const loginTime = getLoginTime()
    const now = new Date().getTime()
    return Math.round((now - loginTime) / 1000)
}

export const usePreviousSignin = (): [boolean, () => void] => {
    const [previousSignin, _setPreviousSignin] = useState<boolean>(
        localStorage.getItem(PREVIOUS_SIGNIN_KEY) === 'true'
    )
    const setPreviousSignin = () => {
        localStorage.setItem(PREVIOUS_SIGNIN_KEY, 'true')
        _setPreviousSignin(true)
    }
    return [previousSignin, setPreviousSignin]
}

export const useCognitoUser = () => {
    const context = useContext(AuthContext)
    if (!context) throw new Error('Cannot use useCognitoUser outside of an <AuthProvider />')
    return context
}

interface AuthPayload {
    at_hash: string
    aud: string
    auth_time: number
    'cognito:username': string
    email: string
    email_verified: boolean
    exp: number
    family_name: string
    given_name: string
    iat: number
    identities: any[]
    iss: string
    jti: string
    nonce: string
    origin_jti: string
    role: string
    sub: string
    token_use: string
    userId: string
}

export const useUserAuthPayload = (): AuthPayload => {
    const { user } = useAuthContext()
    const [authPayload, setAuthPayload] = useState<AuthPayload>({} as AuthPayload)

    useEffect(() => {
        if (!user) return
        const pl = user.getSignInUserSession()?.getIdToken().decodePayload()
        if (pl) {
            setAuthPayload(pl as AuthPayload)
        }
    }, [user])

    return authPayload
}

export const AuthProvider: React.FC = ({ children }) => {
    const [user, setUser] = useState<CognitoUser | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [linkingCompleted, setLinkingCompleted] = useState<boolean>(false)
    const [federatedSigninLoading, setFederatedSigninLoading] = useState<boolean>(false)
    const [previousSignin, setPreviousSignin] = usePreviousSignin()

    useEffect(() => {
        //https://bobbyhadz.com/blog/aws-amplify-already-found-entry
        if (window.location?.search?.match(/Already.found/)) {
            console.warn('Setting link error to true')
            setLinkingCompleted(true)
        }
        Auth.configure(config.auth)
        Auth.currentAuthenticatedUser()
            .then((user) => setUser(user))
            .catch(() => setUser(null))
            .finally(() => setIsLoading(false))
    }, [])

    const clearLinkingCompleted = useCallback(() => {
        setLinkingCompleted(false)
    }, [])

    const values = {
        user,
        setUser,
        auth: Auth,
        linkingCompleted,
        clearLinkingCompleted,
        federatedSigninLoading,
        setFederatedSigninLoading,
        previousSignin,
        setPreviousSignin,
    }

    if (isLoading || federatedSigninLoading) {
        return <PageLoading />
    }

    return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export const useAuthContext = () => {
    return React.useContext(AuthContext)
}

export const useLogout = () => {
    const [loading, setLoading] = useState<boolean>(false)
    const { setUser } = React.useContext(AuthContext)
    const navigate = useNavigate()

    const logout = async () => {
        setLoading(true)
        try {
            await Auth.signOut()
            setUser(null)
            navigate('/signin')
            //fix login as previous user bug? stupid cognito
            window.location.reload()
        } finally {
            setLoading(false)
        }
    }

    return { logout, loading }
}

export const useConfirmUser = () => {
    const { setPreviousSignin } = useAuthContext()
    const { setUser } = useCognitoUser()
    const navigate = useNavigate()

    const [error, setError] = useState<any>()
    const [loading, setLoading] = useState<boolean>(false)

    const clearError = () => {
        setError(null)
    }

    const handleError = (err: any) => {
        logger.error(err)
        switch (err.code) {
            case 'UserNotFoundException': {
                err.message = 'Invalid Username or Password'
                break
            }
            case 'NotAuthorizedException': {
                err.message = 'Invalid Username or Password'
                break
            }
            case 'CodeMismatchException': {
                err.message = 'Incorrect verification code.'
                break
            }
        }
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const confirmSignUp = async (email: string, password: string, code: string) => {
        setLoading(true)
        setError(null)
        try {
            await Auth.confirmSignUp(email, code)
            const user = await Auth.signIn(email, password)
            setUser(user)
            setPreviousSignin()
            navigate('/account')
        } catch (err: any) {
            handleError(err)
        } finally {
            setLoading(false)
        }
    }

    const resendConfirmationCode = async (email: string) => {
        setLoading(true)
        setError(null)
        try {
            await Auth.resendSignUp(email)
        } catch (err) {
            handleError(err)
        } finally {
            setLoading(false)
        }
    }

    return {
        confirmSignUp,
        resendConfirmationCode,
        clearError,
        error,
        loading,
    }
}

export const useNewPassword = () => {
    const { user, setUser } = useCognitoUser()
    const navigate = useNavigate()

    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)

    const handleError = (err: any) => {
        logger.error(err)
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const completeNewPassword = (newPassword: string) => {
        setLoading(true)
        setError(null)
        Auth.completeNewPassword(user, newPassword)
            .then((user) => {
                setUser(user)
                navigate('/home')
            })
            .catch((err) => {
                handleError(err)
            })
            .finally(() => setLoading(false))
    }

    return {
        completeNewPassword,
        loading,
        error,
    }
}

export const useLogin = () => {
    const { clearLinkingCompleted, setPreviousSignin } = useAuthContext()
    const { setUser } = useCognitoUser()
    const navigate = useNavigate()

    const [error, setError] = useState<any>()
    const [loading, setLoading] = useState<boolean>(false)

    const clearError = () => {
        setError(null)
    }

    const handleError = (err: any) => {
        logger.error(err)
        switch (err.code) {
            case 'UserNotConfirmedException': {
                err.message = 'You have not confirmed your email yet.'
                break
            }
            case 'UserNotFoundException': {
                err.message = 'Invalid Username or Password'
                break
            }
            case 'NotAuthorizedException': {
                err.message = 'Invalid Username or Password'
                break
            }
            case 'UserLambdaValidationException': {
                err.message = 'Error logging in'
                break
            }
        }
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const login = async (usernameOrEmail: string, password: string, metadata: any = {}) => {
        clearLinkingCompleted()
        setLoading(true)
        setError(null)
        try {
            const signinResult = await Auth.signIn(usernameOrEmail, password, metadata)
            setUser(signinResult)
            setPreviousSignin()
            setLoginTime()
            if (signinResult.challengeName === 'NEW_PASSWORD_REQUIRED') {
                navigate('/new-password')
            } else {
                navigate('/home')
            }
        } catch (err: any) {
            handleError(err)
        } finally {
            setLoading(false)
        }
    }

    return {
        login,
        clearError,
        handleError,
        error,
        loading,
    }
}

export const useFederatedSignin = () => {
    const { clearLinkingCompleted, setFederatedSigninLoading, setPreviousSignin } = useAuthContext()
    const [error, setError] = useState<any>()

    const handleError = (err: any) => {
        logger.error(err)
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const federatedSignin = async (
        provider: CognitoHostedUIIdentityProvider = CognitoHostedUIIdentityProvider.Google
    ) => {
        setLoginTime()
        setFederatedSigninLoading(true)
        setError(null)
        clearLinkingCompleted()
        try {
            const result = await Auth.federatedSignIn({
                provider,
            })
            setPreviousSignin()
            return result
        } catch (err) {
            handleError(err)
            setFederatedSigninLoading(false)
        }
    }

    return {
        federatedSignin,
        error,
    }
}

export const useSignUp = () => {
    const { setPreviousSignin } = useAuthContext()
    const [error, setError] = useState<any>()
    const [loading, setLoading] = useState<boolean>(false)

    const clearError = () => {
        setError(null)
    }

    const handleError = (err: any) => {
        logger.error(err)
        switch (err.code) {
            case 'InvalidParameterException': {
                if (err.message.slice(0, 8) === 'Username') {
                    //Invalid Email / Username
                    err.code = 'InvalidEmailException'
                    err.message = 'Please enter a valid email.'
                } else {
                    //Invalid password
                    err.code = 'InvalidPasswordException'
                    err.message =
                        'Passwords must have at least 8 characters, a lower and uppercase letter, a special character, and a number.'
                }
                break
            }
            case 'UsernameExistsException': {
                err.message = 'User already exists with this email.'
                break
            }
            case 'InvalidPasswordException': {
                err.message =
                    'Passwords must have at least 8 characters, a lower and uppercase letter, a special character, and a number.'
                break
            }
        }
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const signUp = async (email: string, password: string) => {
        setLoading(true)
        setError(null)
        try {
            const result = await Auth.signUp({
                username: email.toLowerCase(),
                password,
            })
            setPreviousSignin()
            return result
        } catch (err: any) {
            handleError(err)
        } finally {
            setLoading(false)
        }
    }

    return {
        signUp,
        clearError,
        error,
        loading,
    }
}

export const useForgotPassword = () => {
    const { setUser } = useCognitoUser()

    const [error, setError] = useState<any>()
    const [loading, setLoading] = useState<boolean>(false)

    const handleError = (err: AuthError) => {
        logger.error(err)
        switch (err.name) {
            case 'CodeMismatchException':
                err.message = 'Incorrect verification code.'
                break

            case 'InvalidParameterException':
                err.message =
                    'Passwords must have at least 8 characters, a lower and uppercase letter, a special character, and a number.'
                break

            case 'InvalidPasswordException':
                err.message =
                    'Passwords must have at least 8 characters, a lower and uppercase letter, a special character, and a number.'
                break

            case 'UserNotFoundException':
                err.message = 'No user found with that email'

                break
        }
        setError({
            type: err.name,
            message: err.message,
        })
    }

    const sendForgotPasswordEmail = async (email: string) => {
        setLoading(true)
        setError(null)
        try {
            const result = await Auth.forgotPassword(email)
            return result
        } catch (err) {
            handleError(err as AuthError)
        } finally {
            setLoading(false)
        }
    }

    const confirmNewPassword = async (email: string, code: string, newPassword: string) => {
        setLoading(true)
        setError(null)
        try {
            await Auth.forgotPasswordSubmit(email, code, newPassword)
            const user = await Auth.signIn(email, newPassword)
            setUser(user)
            return user
        } catch (err) {
            handleError(err as AuthError)
        } finally {
            setLoading(false)
        }
    }

    return {
        sendForgotPasswordEmail,
        confirmNewPassword,
        error,
        loading,
    }
}
